import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import logo from '@/assets/logo.png';
import wordmark from '@/assets/Tyche-Palabra.png';

const SECTION_LINKS = [
  { label: 'Inicio',      type: 'section', id: 'hero' },
  { label: 'Colecciones', type: 'route',   href: '/catalogo' },
  { label: 'Sobre Tyche', type: 'section', id: 'sobre' },
  { label: 'Testimonios', type: 'section', id: 'testimonios' },
  { label: 'Contacto',    type: 'section', id: 'contacto' },
] as const;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';

  // En páginas internas el header siempre tiene fondo
  const forceBackground = !isHome;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cerrar menú móvil al cambiar de ruta
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    } else {
      navigate(`/#${id}`);
    }
  };

  // Cuando se llega a / con hash, hacer scroll al section
  useEffect(() => {
    if (isHome && location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [isHome, location.hash]);

  const isActive = (link: typeof SECTION_LINKS[number]) => {
    if (link.type === 'route') return location.pathname === link.href;
    return false;
  };

  const handleNavClick = (link: typeof SECTION_LINKS[number]) => {
    if (link.type === 'route') {
      setIsMobileMenuOpen(false);
    } else {
      scrollToSection(link.id);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        forceBackground || isScrolled
          ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-elegant'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <nav className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <img
              src={logo}
              alt="Tyche"
              className="h-16 w-16 lg:h-20 lg:w-20 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <img
              src={wordmark}
              alt="Tyche"
              className="h-9 lg:h-11 w-auto object-contain hidden sm:block"
            />
          </Link>

          {/* Desktop Navigation */}
          <ul className="hidden lg:flex items-center gap-8">
            {SECTION_LINKS.map((link) => (
              <li key={link.label}>
                {link.type === 'route' ? (
                  <Link
                    to={link.href}
                    className={`font-body text-sm tracking-wide uppercase transition-colors duration-300 ${
                      isActive(link)
                        ? 'text-primary border-b border-primary pb-0.5'
                        : 'text-foreground/80 hover:text-primary'
                    }`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    onClick={() => handleNavClick(link)}
                    className="font-body text-sm text-foreground/80 hover:text-primary transition-colors duration-300 tracking-wide uppercase"
                  >
                    {link.label}
                  </button>
                )}
              </li>
            ))}
          </ul>

          {/* CTA Button Desktop */}
          <div className="hidden lg:block">
            <Button
              variant="goldOutline"
              size="sm"
              onClick={() => scrollToSection('contacto')}
            >
              Contactar
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-foreground hover:text-primary transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-96 pb-6' : 'max-h-0'
          }`}
        >
          <ul className="flex flex-col gap-4">
            {SECTION_LINKS.map((link) => (
              <li key={link.label}>
                {link.type === 'route' ? (
                  <Link
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`font-body text-sm tracking-wide uppercase w-full block py-2 transition-colors duration-300 ${
                      isActive(link) ? 'text-primary' : 'text-foreground/80 hover:text-primary'
                    }`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    onClick={() => handleNavClick(link)}
                    className="font-body text-sm text-foreground/80 hover:text-primary transition-colors duration-300 tracking-wide uppercase w-full text-left py-2"
                  >
                    {link.label}
                  </button>
                )}
              </li>
            ))}
            <li className="pt-2">
              <Button
                variant="gold"
                size="default"
                className="w-full"
                onClick={() => scrollToSection('contacto')}
              >
                Contactar
              </Button>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
